import { useState } from 'react';
import Lottie from 'react-lottie';
import { numberWithDot } from 'helpers/utils';
import { CustomSwitch } from 'components/CustomSwitch';
import * as tableSkeletonAnimation from '../../assets/json/table-skeleton.json';

const tableSkeletonOptions = {
  loop: true,
  autoplay: true,
  animationData: tableSkeletonAnimation,
  rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
};

interface Props<T> {
  title: string;
  subtitle: string;
  columns: string[];
  data: T[];
  dimensionOnChange?: (value: string) => void;
  dimensionOptions?: { value: number; label: string }[];
  dimensionValue?: string;
  withPagination?: boolean;
  isLoading?: boolean;
  onPageChange?: (direction: 'next' | 'previous', page: number) => void;
}

export default function RegularTable<
  T extends {
    total?: number;
    donors?: number;
    time?: string;
    name?: string;
    donation_amount?: number;
    ref_code?: string;
  },
>({
  title,
  subtitle,
  columns,
  data,
  dimensionOnChange,
  dimensionOptions,
  dimensionValue,
  withPagination,
  isLoading = false,
  onPageChange,
}: Props<T>) {
  const [currentPage, setCurrentPage] = useState<number>(0);

  if (isLoading) {
    return (
      <>
        <Lottie options={tableSkeletonOptions} />
        <br />
      </>
    );
  }

  const pageSize = 5;

  // Calcular los elementos de la página actual
  const currentPageData =
    data?.slice(currentPage * pageSize, currentPage * pageSize + pageSize) || [];
  const isLastPage = currentPage >= Math.ceil((data?.length || 0) / pageSize) - 1;
  const hasNextPageData =
    data?.slice((currentPage + 1) * pageSize, (currentPage + 1) * pageSize + pageSize)?.length > 0;

  const sortValues = (item: T) => {
    const type = columns[0] === 'Recipient Name' ? 'recipient' : 'page';
    return [item[type], `$${numberWithDot(item.total?.toFixed(2) || 0)}`, item.donors];
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-row space-between">
          <div className="grid-column">
            <span className="oath-h4 inkBlue capitalize">{title}</span>
            <span className="oath-bodysmall gray">{subtitle}</span>
          </div>
          {dimensionOptions && dimensionOnChange && (
            <CustomSwitch
              options={dimensionOptions}
              onSelect={dimensionOnChange}
              value={dimensionValue}
            />
          )}
        </div>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                {columns.map((col: string, i: number) => (
                  <th className="oath-eyebrow inkBlue" key={i}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentPageData?.map((item: T, i: number) => (
                <tr key={i}>
                  {sortValues(item)?.map((val, j) => (
                    <td key={j} className="oath-bodysmall inkBlue">
                      {val || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {withPagination && (
          <div className="oathBlue-pagination">
            <div className="pagination-bottom">
              <div className="-pagination">
                <div className="col-lg-6">
                  <div className="-previous">
                    <button
                      type="button"
                      onClick={() => {
                        const newPage = currentPage - 1;
                        onPageChange?.('previous', newPage);
                        setCurrentPage(newPage);
                      }}
                      disabled={currentPage === 0}
                      className="-btn"
                    >
                      Previous
                    </button>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="-next">
                    <button
                      type="button"
                      onClick={() => {
                        const newPage = currentPage + 1;
                        onPageChange?.('next', newPage);
                        setCurrentPage(newPage);
                      }}
                      disabled={isLastPage || !hasNextPageData}
                      className="-btn"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
